import { api } from '~/trpc-extra/api';
import { useDataTable } from '../common/useDataTable';
import { DataTable } from '../common/DataTable';
import { createRunColumns } from './runColumns';
import { useState, useRef, useEffect } from 'react';
import { Pagination } from '@mantine/core';

// Doing server side pagination to prevent potentially crashing the client with a large number of test runs
export default function RunTable({ testId, runId }: { testId: string; runId?: string }) {
  const utils = api.useUtils();
  const [page, setPage] = useState(1);
  const needsInitialRunPageNumber = useRef(true);
  const ITEMS_PER_PAGE = 10;

  // Fetch total count of test runs
  const { data: totalCount = 0, isLoading: isCountLoading } = api.test.countTestRuns.useQuery(
    { testId },
    { enabled: !!testId },
  );

  // Fetch paginated data with proper limits
  const { data: { paginatedRuns = [], initialRunPageNumber } = {}, isLoading: isDataLoading } =
    api.test.listTestRuns.useQuery(
      {
        testId,
        limit: ITEMS_PER_PAGE,
        skip: (page - 1) * ITEMS_PER_PAGE,
        runId,
        needsInitialRunPageNumber: needsInitialRunPageNumber.current,
      },
      { enabled: !!testId },
    );

  // Set initial page number when we receive it
  useEffect(() => {
    if (needsInitialRunPageNumber.current && initialRunPageNumber !== undefined) {
      setPage(initialRunPageNumber);
      needsInitialRunPageNumber.current = false;
    }
  }, [initialRunPageNumber]);

  // Create the table with the paginated data
  const table = useDataTable({
    columns: createRunColumns(utils),
    data: paginatedRuns,
    // We're handling pagination manually with server-side
    enablePagination: false,
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
  const isLoading = isCountLoading || isDataLoading;

  return (
    <div className="flex h-full flex-col">
      <DataTable
        table={table}
        isLoading={isLoading}
        // highlight the current run
        higlightRow={(row) => row.original.id === runId}
        emptyMessage="No test run history found."
      />
      {totalCount > ITEMS_PER_PAGE && (
        <Pagination
          value={page}
          onChange={setPage}
          total={totalPages}
          className="mx-auto mb-8 mt-4"
        />
      )}
    </div>
  );
}
